/**
 * SoftwareConsole — consola de software del parque, con el diseño pedido.
 *
 * Reproduce el centro de operaciones de referencia: KPIs arriba, cumplimiento
 * + reparto por canal + aplicaciones más instaladas, catálogo a la izquierda con
 * el lanzador de despliegue a la derecha, y abajo despliegues recientes con su
 * progreso y un registro de actividad.
 *
 * Solo Windows: es la única plataforma con agente hoy. Cuando haya Linux/macOS
 * el reparto por canal se pinta solo, sin tocar el diseño.
 *
 * Sin librerías de gráficas: anillos y barras son SVG/CSS con tokens del tema.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import HelpDot from '../academy/HelpDot';
import Dialog from './common/Dialog';

/* ── Paleta y utilidades ─────────────────────────────────────────────────── */
const TONE = {
  ok: '#16a34a',
  warn: '#eab308',
  bad: '#dc2626',
  info: '#2563eb',
  muted: '#94a3b8',
};

const STATUS = {
  done: { label: 'Instalado', color: TONE.ok },
  running: { label: 'En ejecución', color: TONE.info },
  queued: { label: 'En cola', color: TONE.info },
  pending: { label: 'En cola', color: TONE.info },
  error: { label: 'Con errores', color: TONE.bad },
  cancelled: { label: 'Cancelado', color: TONE.muted },
};

const fecha = (v) => {
  if (!v) return '—';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v}Z`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const iniciales = (nombre) => {
  const limpio = String(nombre || '?').replace(/[^A-Za-z0-9 ]/g, ' ').trim();
  const p = limpio.split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[1][0]).toUpperCase();
};

/* ── Piezas de diseño ────────────────────────────────────────────────────── */
const cardBox = {
  border: '1px solid var(--border)',
  borderRadius: 12,
  background: 'var(--bg-card)',
  padding: '12px 14px',
};

const boxTitle = { margin: '0 0 2px', fontSize: 14, fontWeight: 700, textAlign: 'left' };
const boxHint = { margin: '0 0 10px', fontSize: 11.5, color: 'var(--text-muted)' };

function KpiCard({ icon: Icon, color, label, value, hint }) {
  return (
    <div style={{ ...cardBox, display: 'flex', gap: 10, alignItems: 'center' }}>
      <span
        style={{
          width: 40, height: 40, borderRadius: 10, flex: '0 0 auto',
          background: `${color}1a`, color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 20, height: 20 }} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10.5, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05 }}>{value}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
      </div>
    </div>
  );
}

function Ring({ pct, label, sub, color = TONE.ok }) {
  const size = 132;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.min(100, Math.max(0, pct)) / 100) * c;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} role="img" aria-label={label}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 21, fontWeight: 800 }}>{pct}%</span>
        <span style={{ fontSize: 9.5, color: 'var(--text-muted)', letterSpacing: 0.5 }}>{sub}</span>
      </div>
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <span style={{ display: 'block', height: 7, borderRadius: 5, background: 'var(--border)', overflow: 'hidden' }}>
      <span style={{ display: 'block', width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: color }} />
    </span>
  );
}

/* ── Consola ─────────────────────────────────────────────────────────────── */
export default function SoftwareConsole({ companyId }) {
  const [packages, setPackages] = useState([]);
  const [overview, setOverview] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [categoria, setCategoria] = useState('');

  const [selPkg, setSelPkg] = useState('');
  const [allAgents, setAllAgents] = useState(true);
  const [destinos, setDestinos] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', package_id: '', default_version: '', category: 'utilities' });
  // Catálogo predefinido (los paquetes que ya conocemos): se carga al abrir el alta.
  const [catalogo, setCatalogo] = useState([]);
  const [catalogoQ, setCatalogoQ] = useState('');
  const [catalogoCat, setCatalogoCat] = useState('');
  const [catalogoSel, setCatalogoSel] = useState([]);
  // Detalle de cobertura de una aplicación (quién la tiene y quién no).
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleQ, setDetalleQ] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [pk, ov, dp, ho] = await Promise.all([
        api.getSoftwarePackages(companyId),
        api.getSoftwareOverview(companyId),
        api.getSoftwareDeployments(companyId, { limit: 30 }),
        api.getPackageHosts(companyId),
      ]);
      setPackages(pk.packages || pk || []);
      setOverview(ov || null);
      setDeployments(dp.deployments || dp || []);
      setHosts(ho.hosts || ho || []);
    } catch (e) {
      setError(e?.message || 'No se pudo cargar la consola de software');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const deps = overview?.deployments || {};
  const totalDeps = (deps.done || 0) + (deps.error || 0) + (deps.running || 0) + (deps.cancelled || 0);
  const pctOk = totalDeps ? Math.round(((deps.done || 0) / totalDeps) * 100) : 0;
  const activos = (deps.running || 0) + (deps.queued || 0) + (deps.pending || 0);
  const equiposConSoftware = overview?.hosts_with_installs ?? 0;

  const porPaquete = useMemo(() => {
    const b = overview?.by_package;
    const lista = Array.isArray(b) ? b : Object.values(b || {});
    return lista
      .filter((p) => (Number(p.done) || 0) > 0 || (Number(p.total) || 0) > 0)
      .sort((x, y) => (Number(y.done) || 0) - (Number(x.done) || 0));
  }, [overview]);

  const categoriaDe = (p) => p.category || 'otros';

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return packages.filter((p) => {
      if (estado === 'aprobado' && !p.enabled) return false;
      if (estado === 'pendiente' && p.enabled) return false;
      if (categoria && categoriaDe(p) !== categoria) return false;
      if (!t) return true;
      return `${p.name} ${p.package_id} ${p.vendor || ''}`.toLowerCase().includes(t);
    });
  }, [packages, q, estado, categoria]);

  const categorias = useMemo(
    () => Array.from(new Set(packages.map(categoriaDe))).sort(),
    [packages],
  );

  const statsDe = (packageId) => porPaquete.find((x) => String(x.package_id) === String(packageId)) || {};

  const lanzar = async () => {
    if (!selPkg) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = allAgents
        ? { package_id: selPkg, all_agents: true }
        : { package_id: selPkg, targets: destinos.map((ciId) => ({ ci_type: 'workstation', ci_id: ciId })) };
      const res = await api.deploySoftwarePackage(payload, companyId);
      setNotice(`Despliegue encolado en ${res.count ?? (allAgents ? hosts.length : destinos.length)} equipo(s).`);
      setDestinos([]);
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo lanzar el despliegue');
    } finally {
      setBusy(false);
    }
  };

  const reintentar = async (id) => {    setBusy(true);
    try {
      await api.retrySoftwareDeployment(id, companyId);
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo reintentar');
    } finally {
      setBusy(false);
    }
  };

  /** Catálogo predefinido de la casa: los paquetes que ya vienen listos. */
  const cargarCatalogo = async () => {
    try {
      const res = await api.getSoftwareCatalog(companyId);
      setCatalogo(res.catalog || res.packages || res.items || []);
    } catch {
      setCatalogo([]);
    }
  };

  /** Añade al catálogo de la empresa los paquetes marcados del catálogo base. */
  const importarSeleccionados = async () => {
    if (!catalogoSel.length) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api.importSoftwareCatalog({ package_ids: catalogoSel }, companyId);
      setNotice(`${res.created} paquete(s) añadidos${res.already ? `; ${res.already} ya estaban` : ''}.`);
      setCatalogoSel([]);
      await load();
      await cargarCatalogo();
    } catch (e) {
      setError(e?.message || 'No se pudo importar el catálogo');
    } finally {
      setBusy(false);
    }
  };

  /** Alta manual: nombre visible + id de Chocolatey (lo único obligatorio). */
  const crearPaquete = async () => {
    if (!form.name.trim() || !form.package_id.trim()) {
      setError('Hacen falta el nombre y el id de Chocolatey');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.createSoftwarePackage({
        name: form.name.trim(),
        package_id: form.package_id.trim().toLowerCase(),
        default_version: form.default_version.trim() || null,
        category: form.category || 'other',
      }, companyId);
      setNotice(`Paquete «${form.name.trim()}» añadido al catálogo.`);
      setForm({ name: '', package_id: '', default_version: '', category: 'utilities' });
      setShowAdd(false);
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo crear el paquete');
    } finally {
      setBusy(false);
    }
  };

  /** Trae de golpe el catálogo predefinido (los 16 paquetes recomendados). */const importarRecomendados = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api.importSoftwareCatalog({ scope: 'recommended' }, companyId);
      setNotice(`${res.created} paquete(s) añadidos${res.already ? `; ${res.already} ya estaban` : ''}.`);
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo importar el catálogo');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="sf2-hint">Cargando la consola de software…</p>;

  const recientes = deployments.slice(0, 6);

  // Nombres reales: los despliegues traen ids, pero la consola ya tiene el
  // catálogo y los equipos con agente cargados. Resolver aquí evita una
  // llamada extra y evita enseñar «paquete 3» o «workstation 24».
  const nombrePaquete = (d) => (
    d.package_name
    || packages.find((p) => String(p.id) === String(d.package_id))?.name
    || `Paquete ${d.package_id}`
  );
  const nombreEquipo = (d) => (
    d.ci_name
    || d.ci_label
    || hosts.find((h) => String(h.ci_id) === String(d.ci_id) && h.ci_type === d.ci_type)?.name
    || `Equipo ${d.ci_id}`
  );

  /**
   * Cobertura de una aplicación: quién la tiene, quién no y qué porcentaje del
   * parque representa. Se pide solo al abrir el detalle (dos llamadas cortas) en
   * vez de precargar miles de filas.
   */
  const abrirDetalle = async (p) => {
    setDetalle({ pkg: p, installed: [], missing: [], loading: true });
    setDetalleQ('');
    setDetalleLoading(true);
    try {
      const res = await api.getSoftwareDeployments(companyId, {
        package_id: p.package_id, status: 'done', limit: 500,
      });
      const filas = res.deployments || res || [];
      const conSoftware = new Map();
      filas.forEach((d) => {
        if (d.status !== 'done') return;
        conSoftware.set(`${d.ci_type}:${d.ci_id}`, d);
      });
      const instalados = hosts
        .filter((h) => conSoftware.has(`${h.ci_type || 'workstation'}:${h.ci_id}`))
        .map((h) => ({
          ci_type: h.ci_type || 'workstation',
          ci_id: h.ci_id,
          name: h.name,
          version: conSoftware.get(`${h.ci_type || 'workstation'}:${h.ci_id}`)?.installed_version || '—',
        }));
      const faltan = hosts
        .filter((h) => !conSoftware.has(`${h.ci_type || 'workstation'}:${h.ci_id}`))
        .map((h) => ({ ci_type: h.ci_type || 'workstation', ci_id: h.ci_id, name: h.name }));
      setDetalle({ pkg: p, installed: instalados, missing: faltan, loading: false });
    } catch (e) {
      setError(e?.message || 'No se pudo cargar el detalle');
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };


  return (
    <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
      {/* ── Cabecera ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Squares2X2Icon style={{ width: 22, height: 22, color: TONE.info }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 17 }}>Gestión de software</h2>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-muted)' }}>
            Inventario, catálogo y despliegues sobre los equipos con agente
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar aplicación, paquete o fabricante…"
            style={{ minWidth: 280, padding: '7px 10px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg-card)' }}
          />
          <button type="button" className="list-btn-secondary" onClick={load}>
            <ArrowPathIcon style={{ width: 15, height: 15 }} /> Recargar
          </button>
          <button type="button" className="list-btn-primary" onClick={() => { setShowAdd((v) => !v); if (!catalogo.length) cargarCatalogo(); }}>
            {showAdd ? 'Cerrar' : '＋ Añadir paquete'}
          </button>
          <HelpDot topic="agente.paquetes" label="Ayuda: paquetes de Chocolatey" />
        </div>
      </div>

      {/* Alta de paquete: nombre visible + id de Chocolatey. */}
      {showAdd && (
        <div style={{ ...cardBox, display: 'grid', gap: 8 }}>
          <h3 style={{ ...boxTitle, margin: 0 }}>Nuevo paquete del catálogo</h3>
          <p style={boxHint}>
            El <strong>id de Chocolatey</strong> es el nombre real del paquete en el repositorio
            (por ejemplo <code>notepadplusplus</code>, <code>7zip</code>, <code>googlechrome</code>).
            Se comprueba en <code>community.chocolatey.org/packages</code>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr 0.9fr auto', gap: 8, alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5, color: 'var(--text-muted)' }}>
              Nombre visible
              <input
                className="list-search"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Notepad++"
              />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5, color: 'var(--text-muted)' }}>
              Id de Chocolatey
              <input
                className="list-search"
                value={form.package_id}
                onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                placeholder="notepadplusplus"
              />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5, color: 'var(--text-muted)' }}>
              Versión (opcional)
              <input
                className="list-search"
                value={form.default_version}
                onChange={(e) => setForm({ ...form, default_version: e.target.value })}
                placeholder="última"
              />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5, color: 'var(--text-muted)' }}>
              Categoría
              <select
                className="list-select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {['utilities', 'browsers', 'office', 'media', 'communication', 'development', 'security', 'runtimes', 'other']
                  .map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <button type="button" className="list-btn-primary" disabled={busy} onClick={crearPaquete}>
              Añadir
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...boxHint, margin: 0 }}>¿No quieres ir uno a uno?</span>
            <button type="button" className="list-btn-secondary" disabled={busy} onClick={importarRecomendados}>
              Añadir los 16 recomendados
            </button>
          </div>

          {/* Catálogo predefinido: elegir de lo que ya tenemos */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <strong style={{ fontSize: 12.5 }}>
                O elige del catálogo ({catalogo.length})
              </strong>
              <input
                className="list-search"
                placeholder="Buscar en el catálogo…"
                value={catalogoQ}
                onChange={(e) => setCatalogoQ(e.target.value)}
                style={{ minWidth: 200 }}
              />
              <select className="list-select" value={catalogoCat} onChange={(e) => setCatalogoCat(e.target.value)}>
                <option value="">Todas las categorías</option>
                {Array.from(new Set(catalogo.map((c) => c.category).filter(Boolean))).sort()
                  .map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                type="button"
                className="list-btn-primary"
                disabled={busy || catalogoSel.length === 0}
                onClick={importarSeleccionados}
              >
                Añadir seleccionados ({catalogoSel.length})
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 6, maxHeight: 230, overflow: 'auto' }}>
              {catalogo
                .filter((c) => {
                  const yaEsta = packages.some((p) => p.package_id === c.package_id);
                  if (yaEsta) return true;
                  if (catalogoCat && c.category !== catalogoCat) return false;
                  const t = catalogoQ.trim().toLowerCase();
                  if (!t) return true;
                  return `${c.name} ${c.package_id} ${c.vendor || ''}`.toLowerCase().includes(t);
                })
                .map((c) => {
                  const yaEsta = packages.some((p) => p.package_id === c.package_id);
                  const marcado = catalogoSel.includes(c.package_id);
                  return (
                    <label
                      key={c.package_id}
                      title={c.description || c.package_id}
                      style={{
                        display: 'flex', gap: 6, alignItems: 'flex-start', padding: '6px 8px',
                        border: `1px solid ${marcado ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: 8, cursor: yaEsta ? 'default' : 'pointer',
                        opacity: yaEsta ? 0.45 : 1, background: 'var(--bg-card)', fontSize: 11.5,
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={yaEsta}
                        checked={marcado}
                        onChange={(e) => setCatalogoSel((prev) => (
                          e.target.checked ? [...prev, c.package_id] : prev.filter((x) => x !== c.package_id)
                        ))}
                        style={{ marginTop: 2 }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name}
                        </span>
                        <code style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.package_id}</code>
                        {c.recommended ? <span className="sf2-pill sf2-pill--ok" style={{ marginLeft: 4, fontSize: 9 }}>recomendado</span> : null}
                        {yaEsta ? <span className="sf2-pill" style={{ marginLeft: 4, fontSize: 9 }}>ya lo tienes</span> : null}
                      </span>
                    </label>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="sf2-pill sf2-pill--bad" style={{ display: 'inline-block' }}>
          <ExclamationTriangleIcon style={{ width: 13, height: 13, marginRight: 4 }} />{error}
        </div>
      )}
      {notice && !error && (
        <div className="sf2-pill sf2-pill--ok" style={{ display: 'inline-block' }}>
          <CheckCircleIcon style={{ width: 13, height: 13, marginRight: 4 }} />{notice}
        </div>
      )}

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
        <KpiCard icon={Squares2X2Icon} color={TONE.info} label="Aplicaciones en catálogo"
          value={packages.length} hint={`${packages.filter((p) => p.enabled).length} habilitadas`} />
        <KpiCard icon={CheckCircleIcon} color={TONE.ok} label="Instalaciones en el parque"
          value={deps.done || 0} hint={`${pctOk}% de los despliegues`} />
        <KpiCard icon={RocketLaunchIcon} color="#8b5cf6" label="Despliegues activos"
          value={activos} hint={`${deps.running || 0} en ejecución · ${deps.error || 0} con error`} />
        <KpiCard icon={ServerStackIcon} color="#f97316" label="Equipos con software"
          value={equiposConSoftware} hint={`de ${hosts.length} con agente`} />
      </div>

      {/* ── Cumplimiento · Canal · Más instaladas ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(200px, 0.7fr) minmax(300px, 1.4fr)', gap: 10 }}>
        <div style={cardBox}>
          <h3 style={boxTitle}>Cumplimiento del software</h3>
          <p style={boxHint}>Despliegues correctos sobre el total</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Ring pct={pctOk} label="Cumplimiento" sub="CONFORME" color={pctOk >= 80 ? TONE.ok : pctOk >= 50 ? TONE.warn : TONE.bad} />
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 5, fontSize: 12 }}>
              <li><span style={{ color: TONE.ok }}>●</span> Conforme <strong>{deps.done || 0}</strong></li>
              <li><span style={{ color: TONE.info }}>●</span> En curso <strong>{activos}</strong></li>
              <li><span style={{ color: TONE.bad }}>●</span> Con error <strong>{deps.error || 0}</strong></li>
              <li><span style={{ color: TONE.muted }}>●</span> Cancelado <strong>{deps.cancelled || 0}</strong></li>
            </ul>
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Canal de distribución</h3>
          <p style={boxHint}>Por plataforma</p>
          <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                <span>Windows (Chocolatey)</span><strong>100%</strong>
              </div>
              <Bar pct={100} color={TONE.info} />
            </div>
            {['Linux (apt/dnf)', 'macOS (brew)'].map((p) => (
              <div key={p} style={{ opacity: 0.45 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                  <span>{p}</span><span>sin agente</span>
                </div>
                <Bar pct={0} color={TONE.muted} />
              </div>
            ))}
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Aplicaciones más instaladas</h3>
          <p style={boxHint}>Instalaciones correctas sobre el total del paquete</p>
          <div style={{ display: 'grid', gap: 7 }}>
            {porPaquete.slice(0, 5).map((p, i) => {
              const done = Number(p.done) || 0;
              const total = Number(p.total) || done || 1;
              const pct = Math.round((done / Math.max(hosts.length, 1)) * 100);
              const color = [TONE.info, TONE.ok, '#f97316', '#8b5cf6', '#0891b2'][i % 5];
              return (
                <div key={p.package_id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 90px', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: color, color: '#fff', fontSize: 9.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {iniciales(p.name)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <button
                      type="button"
                      onClick={() => abrirDetalle(p)}
                      title="Ver quién la tiene y a quién le falta"
                      style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-l, #2563eb)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.name}
                      </div>
                      <Bar pct={pct} color={color} />
                    </button>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
                    {pct}% ({done}/{total})
                  </span>
                </div>
              );
            })}
            {porPaquete.length === 0 && <p style={boxHint}>Todavía no hay instalaciones.</p>}
          </div>
        </div>
      </div>

      {/* ── Catálogo + lanzador ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 1.9fr) minmax(300px, 1fr)', gap: 10 }}>
        <div style={cardBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <h3 style={{ ...boxTitle, margin: 0, flex: 1 }}>Catálogo de software</h3>
            <select value={estado} onChange={(e) => setEstado(e.target.value)} className="list-select">
              <option value="">Estado: todos</option>
              <option value="aprobado">Habilitados</option>
              <option value="pendiente">Deshabilitados</option>
            </select>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="list-select">
              <option value="">Categoría: todas</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ overflow: 'auto', maxHeight: 320 }}>
            <table className="list-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Aplicación</th><th>Id Chocolatey</th><th>Versión</th><th>Instalación</th><th>Estado</th><th />
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p) => {
                  const st = statsDe(p.id);
                  const done = Number(st.done) || 0;
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td><code style={{ fontSize: 11 }}>{p.package_id}</code></td>
                      <td>{p.default_version || 'última'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{done} ok · {Number(st.error) || 0} err</td>
                      <td>
                        <span className={`sf2-pill ${p.enabled ? 'sf2-pill--ok' : 'sf2-pill--wa'}`} style={{ fontSize: 10.5 }}>
                          {p.enabled ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="list-btn-secondary"
                          disabled={!p.enabled || busy}
                          onClick={() => { setSelPkg(String(p.id)); setAllAgents(true); }}
                        >
                          Instalar
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtrados.length === 0 && (
                  <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Sin paquetes con esos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Despliegue de software</h3>
          <p style={boxHint}>Elige el paquete y los equipos</p>

          <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 3 }}>
            Aplicación
          </label>
          <select
            className="list-select"
            style={{ width: '100%', marginBottom: 10 }}
            value={selPkg}
            onChange={(e) => setSelPkg(e.target.value)}
          >
            <option value="">Selecciona un paquete…</option>
            {packages.filter((p) => p.enabled).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
            <input type="checkbox" checked={allAgents} onChange={(e) => setAllAgents(e.target.checked)} />
            Todos los equipos con agente ({hosts.length})
          </label>

          {!allAgents && (
            <div style={{ maxHeight: 130, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 6, marginBottom: 8 }}>
              {hosts.map((h) => (
                <label key={h.ci_id} style={{ display: 'flex', gap: 6, fontSize: 12, padding: '2px 0' }}>
                  <input
                    type="checkbox"
                    checked={destinos.includes(h.ci_id)}
                    onChange={(e) => setDestinos((prev) => (
                      e.target.checked ? [...prev, h.ci_id] : prev.filter((x) => x !== h.ci_id)
                    ))}
                  />
                  {h.name}
                </label>
              ))}
            </div>
          )}

          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
            Se instalará en: <strong>{allAgents ? `los ${hosts.length} equipos con agente` : `${destinos.length} seleccionados`}</strong>
          </div>

          <button
            type="button"
            className="list-btn-primary"
            style={{ width: '100%' }}
            disabled={!selPkg || busy || (!allAgents && destinos.length === 0)}
            onClick={lanzar}
          >
            <RocketLaunchIcon style={{ width: 15, height: 15, marginRight: 5 }} />
            {busy ? 'Lanzando…' : 'Lanzar despliegue'}
          </button>

          <button
            type="button"
            className="list-btn-secondary"
            style={{ width: '100%', marginTop: 8 }}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const res = await api.upgradeAllSoftware({ all_agents: true }, companyId);
                setNotice(`Actualización de paquetes encolada en ${res.count || 0} equipo(s).`);
                await load();
              } catch (e) {
                setError(e?.message || 'No se pudo lanzar');
              } finally { setBusy(false); }
            }}
          >
            <ArrowUpTrayIcon style={{ width: 15, height: 15, marginRight: 5 }} />
            Actualizar todo (Choco)
          </button>
        </div>
      </div>

      {/* ── Despliegues recientes + actividad ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 1.9fr) minmax(300px, 1fr)', gap: 10 }}>
        <div style={cardBox}>
          <h3 style={boxTitle}>Despliegues recientes</h3>
          <p style={boxHint}>Últimos movimientos del parque</p>
          <table className="list-table" style={{ fontSize: 12 }}>
            <thead>
              <tr><th>Fecha</th><th>Aplicación</th><th>Equipo</th><th>Estado</th><th>Progreso</th><th /></tr>
            </thead>
            <tbody>
              {recientes.map((d) => {
                const meta = STATUS[d.status] || { label: d.status, color: TONE.muted };
                const pct = d.status === 'done' ? 100 : d.status === 'running' ? 60 : d.status === 'error' ? 40 : 0;
                return (
                  <tr key={d.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fecha(d.created_at)}</td>
                    <td style={{ fontWeight: 600 }}>{nombrePaquete(d)}</td>
                    <td style={{ fontSize: 11.5 }}>{nombreEquipo(d)}</td>
                    <td><span className="sf2-pill" style={{ background: `${meta.color}1a`, color: meta.color, fontSize: 10.5 }}>{meta.label}</span></td>
                    <td style={{ minWidth: 90 }}><Bar pct={pct} color={meta.color} /></td>
                    <td>
                      {d.status === 'error' && (
                        <button type="button" className="list-action-btn" title="Reintentar" onClick={() => reintentar(d.id)}>
                          <ArrowPathIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {recientes.length === 0 && (
                <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Sin despliegues todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Registros y actividad</h3>
          <p style={boxHint}>Lo último que ha pasado</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6, fontSize: 11.5 }}>
            {deployments.slice(0, 7).map((d) => {
              const meta = STATUS[d.status] || { label: d.status, color: TONE.muted };
              return (
                <li key={`log-${d.id}`} style={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 8, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{fecha(d.created_at).split(',')[0]}</span>
                  <span>
                    <strong style={{ color: meta.color }}>{meta.label}</strong>{' '}
                    {nombrePaquete(d)} · {nombreEquipo(d)}
                    {d.error ? ` · ${String(d.error).slice(0, 60)}` : ''}
                  </span>
                </li>
              );
            })}
            {deployments.length === 0 && <li style={{ color: 'var(--text-muted)' }}>Sin actividad.</li>}
          </ul>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
        <MagnifyingGlassIcon style={{ width: 12, height: 12, marginRight: 4 }} />
        Solo Windows: es la única plataforma con agente propio. Los canales Linux y macOS se activarán cuando haya agente.
      </p>

      {/* ── Detalle de cobertura de una aplicación ── */}
      {detalle && (
        <Dialog
          title={`${detalle.pkg?.name || 'Aplicación'} · cobertura`}
          icon={<ServerStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          onClose={() => setDetalle(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
              {detalle.missing?.length > 0 && (
                <button
                  type="button"
                  className="list-btn-primary"
                  disabled={busy}
                  onClick={() => {
                    setAllAgents(false);
                    setDestinos(detalle.missing.map((m) => m.ci_id));
                    setSelPkg(String(detalle.pkg.package_id));
                    setDetalle(null);
                    setNotice('Destinos cargados en el lanzador: pulsa «Lanzar despliegue».');
                  }}
                >
                  Preparar despliegue en los {detalle.missing.length} que faltan
                </button>
              )}
            </>
          )}
        >
          {detalleLoading ? (
            <p className="sf2-hint">Cargando cobertura…</p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                <div style={{ ...cardBox, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>LO TIENEN</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: TONE.ok }}>{detalle.installed.length}</div>
                </div>
                <div style={{ ...cardBox, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>LES FALTA</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: TONE.warn }}>{detalle.missing.length}</div>
                </div>
                <div style={{ ...cardBox, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>COBERTURA</div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>
                    {hosts.length ? Math.round((detalle.installed.length / hosts.length) * 100) : 0}%
                  </div>
                </div>
              </div>

              <input
                className="list-search"
                placeholder="Buscar equipo…"
                value={detalleQ}
                onChange={(e) => setDetalleQ(e.target.value)}
                style={{ width: '100%', marginBottom: 8 }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, maxHeight: 320, overflow: 'auto' }}>
                <div>
                  <h4 style={{ margin: '0 0 4px', fontSize: 12 }}>Lo tienen ({detalle.installed.length})</h4>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 11.5 }}>
                    {detalle.installed
                      .filter((x) => !detalleQ || x.name.toLowerCase().includes(detalleQ.toLowerCase()))
                      .slice(0, 200)
                      .map((x) => (
                        <li key={`i-${x.ci_id}`} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', padding: '2px 0' }}>
                          <span>{x.name}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{x.version}</span>
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px', fontSize: 12 }}>Les falta ({detalle.missing.length})</h4>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 11.5 }}>
                    {detalle.missing
                      .filter((x) => !detalleQ || x.name.toLowerCase().includes(detalleQ.toLowerCase()))
                      .slice(0, 200)
                      .map((x) => (
                        <li key={`m-${x.ci_id}`} style={{ borderBottom: '1px solid var(--border)', padding: '2px 0', color: 'var(--text-secondary)' }}>
                          {x.name}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
              {detalle.installed.length > 200 || detalle.missing.length > 200 ? (
                <p className="sf2-hint" style={{ marginBottom: 0 }}>
                  Se muestran 200 por lista. Usa el buscador para llegar a un equipo concreto.
                </p>
              ) : null}
            </>
          )}
        </Dialog>
      )}
    </div>
  );
}
